import React, { useState, useRef, useEffect } from 'react';
import { AlertCircle, Plus, X, Edit2, Trash, Download, Video, Zap } from 'react-feather';
import api from './api';
import './Workspace2.css';
import { trackEvent } from './utils/amplitude';

const VideoManagement = ({ disableCuts = false }) => {
    const [activeTab, setActiveTab] = useState('raw'); // 'raw' or 'generated'
    const [videos, setVideos] = useState([]);
    const [generatedVideos, setGeneratedVideos] = useState([]);
    const [uploadProgress, setUploadProgress] = useState({});
    const [isDragging, setIsDragging] = useState(false);
    const [editingVideo, setEditingVideo] = useState(null);
    const [tagInputs, setTagInputs] = useState({}); // Map of videoId to tag input value
    const [newDescription, setNewDescription] = useState('');
    const [errors, setErrors] = useState([]);
    const fileInputRef = useRef(null);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [newCut, setNewCut] = useState({ name: '', url: '' });
    const [isLoading, setIsLoading] = useState(true);
    
    // Upload tracking state
    const [uploadStats, setUploadStats] = useState({
        totalFiles: 0,
        currentFileIndex: 0,
        completedFiles: 0,
        failedFiles: 0,
        currentFileName: ''
    });

    // Shimmer placeholder component
    const ShimmerCard = () => (
        <div className="shimmer-card">
            <div className="shimmer-preview"></div>
            <div className="shimmer-content">
                <div className="shimmer-title shimmer"></div>
                <div className="shimmer-tags">
                    <div className="shimmer-tag shimmer"></div>
                    <div className="shimmer-tag shimmer"></div>
                    <div className="shimmer-tag shimmer"></div>
                </div>
                <div className="shimmer-description shimmer"></div>
            </div>
        </div>
    );

    // Fetch videos on component mount
    useEffect(() => {
        const fetchVideos = async () => {
            setIsLoading(true);
            try {
                const response = await api.getVideos();
                trackEvent('videos_fetched', {
                    count: response.data.length,
                    tab: activeTab
                });
                console.log('Fetched videos:', response.data);
                // Sort videos by createdAt in descending order (newest first)
                const sortedVideos = response.data
                    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                    .map(video => ({
                        _id: video._id,
                        name: video.title,
                        url: video.url,
                        status: video.status,
                        tags: video.tags,
                        description: video.description,
                        cuts: video.cuts,
                        createdAt: video.createdAt
                    }));
                setVideos(sortedVideos);
            } catch (error) {
                trackEvent('videos_fetch_error', {
                    error: error.message,
                    tab: activeTab
                });
                console.error('Error fetching videos:', error);
                setErrors(prev => [...prev, 'Failed to fetch videos: ' + (error.response?.data?.error || error.message)]);
            } finally {
                setIsLoading(false);
            }
        };

        const fetchGeneratedVideos = async () => {
            try {
                const response = await api.getUserGeneratedVideos();
                trackEvent('generated_videos_fetched', {
                    count: response.data.length
                });
                setGeneratedVideos(response.data);
            } catch (error) {
                trackEvent('generated_videos_fetch_error', {
                    error: error.message
                });
                console.error('Error fetching generated videos:', error);
                setErrors(prev => [...prev, 'Failed to fetch generated videos: ' + (error.response?.data?.error || error.message)]);
            }
        };

        fetchVideos();
        fetchGeneratedVideos();
    }, []);

    // Handle file drop
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        
        const files = Array.from(e.dataTransfer.files).filter(file => 
            file.type.startsWith('video/') || file.type.startsWith('image/')
        );

        trackEvent('files_dropped', {
            count: files.length,
            types: files.map(f => f.type)
        });

        if (files.length > 50) {
            trackEvent('files_drop_error', {
                error: 'Too many files',
                count: files.length
            });
            setErrors(prev => [...prev, 'You can only upload up to 50 files at once']);
            return;
        }

        handleFiles(files);
    };

    // Handle file selection
    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        trackEvent('files_selected', {
            count: files.length,
            types: files.map(f => f.type)
        });
        handleFiles(files);
    };

    // Process files
    const handleFiles = async (files) => {
        const uploadErrors = [];
        const failedFiles = new Set();

        // Initialize upload stats
        setUploadStats({
            totalFiles: files.length,
            currentFileIndex: 0,
            completedFiles: 0,
            failedFiles: 0,
            currentFileName: files[0]?.name || ''
        });

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            
            // Update current file being processed
            setUploadStats(prev => ({
                ...prev,
                currentFileIndex: i + 1,
                currentFileName: file.name
            }));

            console.log('Processing file:', {
                name: file.name,
                size: file.size,
                type: file.type,
                lastModified: file.lastModified,
                progress: `${i + 1}/${files.length}`
            });
            
            // Check file size
            if (file.size > 500 * 1024 * 1024) { // 500MB
                uploadErrors.push(`File ${file.name} is too large. Maximum size is 500MB`);
                setUploadStats(prev => ({
                    ...prev,
                    failedFiles: prev.failedFiles + 1
                }));
                continue;
            }

            try {
                trackEvent('file_upload_started', {
                    fileName: file.name,
                    fileSize: file.size,
                    fileType: file.type,
                    fileIndex: i + 1,
                    totalFiles: files.length
                });

                // Create FormData
                const formData = new FormData();
                formData.append('video', file);

                console.log('Starting upload for:', file.name);
                const response = await api.uploadVideo(formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    timeout: 300000, // 5 minutes
                    maxContentLength: Infinity,
                    maxBodyLength: Infinity,
                    onUploadProgress: (progressEvent) => {
                        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        console.log(`Upload progress for ${file.name}: ${progress}% (${i + 1}/${files.length})`);
                        setUploadProgress(prev => ({
                            ...prev,
                            [file.name]: progress
                        }));
                    }
                });

                trackEvent('file_upload_success', {
                    fileName: file.name,
                    fileSize: file.size,
                    fileType: file.type,
                    fileIndex: i + 1,
                    totalFiles: files.length
                });

                // Update video state
                setVideos(prev => prev.map(v => 
                    v._id === response.data.video._id
                        ? {
                            ...v,
                            name: response.data.video.title,
                            description: response.data.video.description,
                            tags: response.data.video.tags,
                            cuts: response.data.video.cuts,
                            status: response.data.video.status
                        }
                        : v
                ));

                // Clear progress after successful upload
                setUploadProgress(prev => {
                    const newProgress = { ...prev };
                    delete newProgress[file.name];
                    return newProgress;
                });

                // Update completed files count
                setUploadStats(prev => ({
                    ...prev,
                    completedFiles: prev.completedFiles + 1
                }));
            } catch (error) {
                trackEvent('file_upload_error', {
                    fileName: file.name,
                    error: error.message,
                    fileIndex: i + 1,
                    totalFiles: files.length
                });
                console.error('Upload error details:', {
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    error: error.response?.data || error.message,
                    status: error.response?.status,
                    headers: error.response?.headers,
                    request: error.config,
                    progress: `${i + 1}/${files.length}`
                });
                uploadErrors.push(`Failed to upload ${file.name}: ${error.response?.data?.error || error.message}`);
                failedFiles.add(file.name);
                
                // Clear progress for failed upload
                setUploadProgress(prev => {
                    const newProgress = { ...prev };
                    delete newProgress[file.name];
                    return newProgress;
                });

                // Update failed files count
                setUploadStats(prev => ({
                    ...prev,
                    failedFiles: prev.failedFiles + 1
                }));
            }
        }

        // Clear upload stats after all files are processed
        setTimeout(() => {
            setUploadStats({
                totalFiles: 0,
                currentFileIndex: 0,
                completedFiles: 0,
                failedFiles: 0,
                currentFileName: ''
            });
        }, 3000); // Keep stats visible for 3 seconds after completion

        if (uploadErrors.length > 0) {
            setErrors(prev => [...prev, ...uploadErrors]);
        }
    };

    // Clear errors
    const clearErrors = () => {
        setErrors([]);
    };

    // Clear individual error
    const clearError = (index) => {
        setErrors(prev => prev.filter((_, i) => i !== index));
    };

    // Handle tag addition
    const handleAddTag = async (videoId) => {
        const tagValue = tagInputs[videoId];
        if (!tagValue?.trim()) return;

        trackEvent('tag_add_attempt', {
            videoId,
            tag: tagValue.trim()
        });

        const trimmedTag = tagValue.trim();

        // Optimistically update the UI
        setVideos(prev => prev.map(video => {
            if (video._id === videoId) {
                return {
                    ...video,
                    tags: [...video.tags, trimmedTag]
                };
            }
            return video;
        }));

        // Clear the input immediately
        setTagInputs(prev => ({
            ...prev,
            [videoId]: ''
        }));

        try {
            await api.addVideoTag(videoId, trimmedTag);
            trackEvent('tag_add_success', {
                videoId,
                tag: trimmedTag
            });
            // No need to update UI again since we did it optimistically
        } catch (error) {
            trackEvent('tag_add_error', {
                videoId,
                tag: trimmedTag,
                error: error.message
            });
            console.error('Error adding tag:', error);
            // If there's an error, remove the tag from the UI
            setVideos(prev => prev.map(video => {
                if (video._id === videoId) {
                    return {
                        ...video,
                        tags: video.tags.filter(tag => tag !== trimmedTag)
                    };
                }
                return video;
            }));
            setErrors(prev => [...prev, `Failed to add tag: ${error.response?.data?.error || error.message}`]);
        }
    };

    // Handle tag removal
    const handleRemoveTag = async (videoId, tagToRemove) => {
        trackEvent('tag_remove_attempt', {
            videoId,
            tag: tagToRemove
        });

        try {
            await api.removeVideoTag(videoId, tagToRemove);
            trackEvent('tag_remove_success', {
                videoId,
                tag: tagToRemove
            });
            
            // Update local state
            setVideos(prev => prev.map(video => {
                if (video._id === videoId) {
                    return {
                        ...video,
                        tags: video.tags.filter(tag => tag !== tagToRemove)
                    };
                }
                return video;
            }));
        } catch (error) {
            trackEvent('tag_remove_error', {
                videoId,
                tag: tagToRemove,
                error: error.message
            });
            console.error('Error removing tag:', error);
            setErrors(prev => [...prev, `Failed to remove tag: ${error.response?.data?.error || error.message}`]);
        }
    };

    // Handle description update
    const handleUpdateDescription = async (videoId) => {
        trackEvent('description_update_attempt', {
            videoId,
            descriptionLength: newDescription.length
        });

        try {
            // Optimistically update the UI
            setVideos(prev => prev.map(video => {
                if (video._id === videoId) {
                    return {
                        ...video,
                        description: newDescription
                    };
                }
                return video;
            }));

            // Close the edit mode immediately
            setEditingVideo(null);
            setNewDescription('');

            // Make the API call in the background
            await api.updateVideoDescription(videoId, newDescription);
            trackEvent('description_update_success', {
                videoId,
                descriptionLength: newDescription.length
            });
            // No need to update UI again since we did it optimistically
        } catch (error) {
            trackEvent('description_update_error', {
                videoId,
                error: error.message
            });
            console.error('Error updating description:', error);
            // If there's an error, revert the description
            setVideos(prev => prev.map(video => {
                if (video._id === videoId) {
                    return {
                        ...video,
                        description: video.description // Revert to original description
                    };
                }
                return video;
            }));
            // Reopen edit mode with the original description
            setEditingVideo(videoId);
            setNewDescription(videos.find(v => v._id === videoId)?.description || '');
            setErrors(prev => [...prev, `Failed to update description: ${error.response?.data?.error || error.message}`]);
        }
    };

    // Handle video deletion
    const handleDeleteVideo = async (videoId) => {
        if (!window.confirm('Are you sure you want to delete this video?')) {
            trackEvent('video_delete_cancelled', { videoId });
            return;
        }

        trackEvent('video_delete_attempt', { videoId });

        // Optimistically remove the video from the UI
        setVideos(prev => prev.filter(video => video._id !== videoId));

        try {
            await api.deleteVideo(videoId);
            trackEvent('video_delete_success', { videoId });
            // No need to update UI again since we did it optimistically
        } catch (error) {
            trackEvent('video_delete_error', {
                videoId,
                error: error.message
            });
            console.error('Error deleting video:', error);
            // If there's an error, add the video back to the list
            const videoToRestore = videos.find(v => v._id === videoId);
            if (videoToRestore) {
                setVideos(prev => [...prev, videoToRestore]);
            }
            setErrors(prev => [...prev, `Failed to delete video: ${error.response?.data?.error || error.message}`]);
        }
    };

    // Handle video download
    const handleDownloadVideo = async (video) => {
        trackEvent('video_download_attempt', {
            videoId: video._id,
            videoName: video.name
        });

        try {
            // Get a pre-signed URL for the video
            const response = await api.getVideoDownloadUrl(video._id);
            const downloadUrl = response.data.url;
            
            // Fetch the video as a blob
            const videoResponse = await fetch(downloadUrl);
            const blob = await videoResponse.blob();
            
            // Create a blob URL
            const blobUrl = window.URL.createObjectURL(blob);
            
            // Create a temporary anchor element
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = video.name || video.title || 'video.mp4';
            link.style.display = 'none';
            
            // Append to body, click, and cleanup
            document.body.appendChild(link);
            link.click();
            
            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);

            trackEvent('video_download_success', {
                videoId: video._id,
                videoName: video.name
            });
        } catch (error) {
            trackEvent('video_download_error', {
                videoId: video._id,
                error: error.message
            });
            console.error('Error downloading video:', error);
            setErrors(prev => [...prev, `Failed to download video: ${error.message}`]);
        }
    };

    // Handle cut addition
    const handleAddCut = async (e, videoId) => {
        e.preventDefault();
        if (!newCut.name.trim() || !newCut.url.trim()) {
            setErrors(['Please provide both name and URL for the cut']);
            return;
        }

        try {
            const response = await api.addVideoCut(videoId, newCut);
            
            // Update local state
            setVideos(prev => prev.map(video => 
                video._id === videoId
                    ? { ...video, cuts: [...video.cuts, response.data] }
                    : video
            ));

            setSelectedVideo(null);
            setNewCut({ name: '', url: '' });
        } catch (error) {
            setErrors(['Failed to add cut: ' + (error.response?.data?.error || error.message)]);
        }
    };

    // Handle cut removal
    const handleRemoveCut = async (videoId, cutId) => {
        try {
            await api.removeVideoCut(videoId, cutId);
            
            // Update local state
            setVideos(prev => prev.map(video => 
                video._id === videoId
                    ? { ...video, cuts: video.cuts.filter(cut => cut.id !== cutId) }
                    : video
            ));
        } catch (error) {
            setErrors(['Failed to remove cut: ' + (error.response?.data?.error || error.message)]);
        }
    };

    // Handle file drop for cuts
    const handleCutDrop = async (e) => {
        e.preventDefault();
        setIsDragging(false);
        
        const files = Array.from(e.dataTransfer.files).filter(file => 
            file.type.startsWith('video/')
        );

        if (files.length > 50) {
            setErrors(['You can only upload up to 50 files at once']);
            return;
        }

        for (const file of files) {
            try {
                const formData = new FormData();
                formData.append('cut', file);
                formData.append('name', file.name);

                const response = await api.addVideoCut(selectedVideo._id, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
                
                // Update local state
                setVideos(prev => prev.map(video => 
                    video._id === selectedVideo._id
                        ? { ...video, cuts: [...(video.cuts || []), response.data] }
                        : video
                ));
            } catch (error) {
                setErrors(['Failed to add cut: ' + (error.response?.data?.error || error.message)]);
            }
        }

        setSelectedVideo(null);
    };

    // Handle tab change
    const handleTabChange = (tab) => {
        trackEvent('tab_changed', {
            from: activeTab,
            to: tab
        });
        setActiveTab(tab);
    };

    return (
        <div className="video-management">
            {/* Tabs */}
            <div className="workspace-tabs">
                <button 
                    className={`tab-button ${activeTab === 'raw' ? 'active' : ''}`}
                    onClick={() => handleTabChange('raw')}
                >
                    <Video size={16} />
                    Raw Videos
                </button>
                <button 
                    className={`tab-button ${activeTab === 'generated' ? 'active' : ''}`}
                    onClick={() => handleTabChange('generated')}
                >
                    <Zap size={16} />
                    Generated Videos
                </button>
            </div>

            {/* Error Messages */}
            {errors.length > 0 && (
                <div className="error-container">
                    {errors.map((error, index) => (
                        <div key={`error-${index}`} className="error-banner" style={{display: 'flex', alignItems: 'center', marginBottom: '10px'}}>
                            <AlertCircle size={20} style={{marginRight: '5px'}} />
                            {error}
                            <button 
                                className="error-close"
                                onClick={() => clearError(index)}
                            >
                                <X size={14} />
                            </button>
                        </div>
                    ))}
                    <button 
                        className="clear-errors"
                        onClick={clearErrors}
                    >
                        Clear All Errors
                    </button>
                </div>
            )}

            {activeTab === 'raw' ? (
                <>
                    {/* Upload Area */}
                    <div 
                        className={`upload-area ${isDragging ? 'dragging' : ''}`}
                        onDragOver={(e) => {
                            e.preventDefault();
                            setIsDragging(true);
                        }}
                        onDragLeave={() => setIsDragging(false)}
                        onDrop={handleDrop}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept="video/*,image/*"
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                        />
                        <label 
                            className="upload-label"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <Plus size={24} />
                            <span>Drag & drop videos here or click to upload</span>
                            <span className="upload-hint">Up to 50 files at once</span>
                        </label>
                    </div>

                    {/* Upload Progress */}
                    {Object.keys(uploadProgress).length > 0 && (
                        <div className="upload-progress">
                            {/* Upload Summary */}
                            {uploadStats.totalFiles > 0 && (
                                <div className="upload-summary" style={{
                                    padding: '12px',
                                    marginBottom: '16px',
                                    backgroundColor: 'var(--input-bg)',
                                    borderRadius: '8px',
                                    border: '1px solid var(--input-border)',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: '8px'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ fontWeight: 'bold', color: 'var(--text-color)' }}>
                                            Uploading: {uploadStats.currentFileIndex} of {uploadStats.totalFiles}
                                        </span>
                                        {uploadStats.currentFileName && (
                                            <span style={{ 
                                                color: 'var(--text-secondary)',
                                                fontSize: '14px',
                                                maxWidth: '200px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {uploadStats.currentFileName}
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', gap: '16px', fontSize: '14px' }}>
                                        <span style={{ color: '#4CAF50' }}>
                                            ✓ {uploadStats.completedFiles} completed
                                        </span>
                                        {uploadStats.failedFiles > 0 && (
                                            <span style={{ color: '#f44336' }}>
                                                ✗ {uploadStats.failedFiles} failed
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                            
                            {/* Individual File Progress */}
                            {Object.entries(uploadProgress).map(([fileName, progress]) => (
                                <div key={`progress-${fileName}`} className="progress-item">
                                    <span>{fileName}</span>
                                    <div className="progress-bar">
                                        <div 
                                            className="progress-fill"
                                            style={{ width: `${progress}%` }}
                                        />
                                    </div>
                                    <span>{progress}%</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Video Grid */}
                    {isLoading ? (
                        <div className="shimmer-grid">
                            {[...Array(6)].map((_, index) => (
                                <ShimmerCard key={`shimmer-${index}`} />
                            ))}
                        </div>
                    ) : (
                        <div className="video-grid">
                            {videos.map(video => (
                                <div key={video._id} className="video-card">
                                    <div className="video-preview" style={{objectFit: 'contain'}}>
                                        <video src={video.url} controls style={{objectFit: 'contain'}}/>
                                        <div className="video-status">
                                            {video.status === 'processing' ? (
                                                <span className="status processing" style={{padding: '2px 4px', borderRadius: '4px'}}>Processing...</span>
                                            ) : (
                                                <span className="status processed" style={{padding: '2px 4px', borderRadius: '4px'}}>Processed</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="video-info">
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginBottom: '12px',
                                            alignContent: 'center'
                                        }}>
                                            <h3 style={{margin: '0'}}>{video.name}</h3>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button 
                                                    className="download-video-btn"
                                                    onClick={() => handleDownloadVideo(video)}
                                                    style={{
                                                        backgroundColor: 'var(--button-bg)',
                                                        color: 'var(--button-text)',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        padding: '4px 8px',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px',
                                                        opacity: '0.9',
                                                        transition: 'opacity 0.2s',
                                                        marginLeft: '10px'
                                                    }}
                                                    onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
                                                    onMouseOut={(e) => e.currentTarget.style.opacity = '0.9'}
                                                >
                                                    <Download size={14} />
                                                </button>
                                                <button 
                                                    className="delete-video-btn"
                                                    onClick={() => handleDeleteVideo(video._id)}
                                                    style={{
                                                        backgroundColor: 'var(--input-bg)',
                                                        color: 'var(--text-color)',
                                                        border: '1px solid var(--input-border)',
                                                        borderRadius: '4px',
                                                        padding: '4px 8px',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px',
                                                        opacity: '0.9',
                                                        transition: 'all 0.2s'
                                                    }}
                                                    onMouseOver={(e) => {
                                                        e.currentTarget.style.opacity = '1';
                                                        e.currentTarget.style.backgroundColor = '#ff4444';
                                                        e.currentTarget.style.color = 'white';
                                                        e.currentTarget.style.borderColor = '#ff4444';
                                                    }}
                                                    onMouseOut={(e) => {
                                                        e.currentTarget.style.opacity = '0.9';
                                                        e.currentTarget.style.backgroundColor = 'var(--input-bg)';
                                                        e.currentTarget.style.color = 'var(--text-color)';
                                                        e.currentTarget.style.borderColor = 'var(--input-border)';
                                                    }}
                                                >
                                                    <Trash size={14} />
                                                </button>
                                            </div>
                                        </div>
                                        
                                        {/* Tags */}
                                        <div className="video-tags">
                                            {video.tags.map(tag => (
                                                <span key={`${video._id}-${tag}`} className="tag">
                                                    {tag}
                                                    <button 
                                                        className="remove-tag"
                                                        onClick={() => handleRemoveTag(video._id, tag)}
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </span>
                                            ))}
                                            <div className="add-tag">
                                                <input
                                                    type="text"
                                                    value={tagInputs[video._id] || ''}
                                                    onChange={(e) => setTagInputs(prev => ({
                                                        ...prev,
                                                        [video._id]: e.target.value
                                                    }))}
                                                    placeholder="Add tag..."
                                                    onKeyPress={(e) => {
                                                        if (e.key === 'Enter') {
                                                            handleAddTag(video._id);
                                                        }
                                                    }}
                                                />
                                                <button onClick={() => handleAddTag(video._id)}>
                                                    <Plus size={14} />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Description */}
                                        <div className="video-description">
                                            {editingVideo === video._id ? (
                                                <div className="edit-description">
                                                    <textarea
                                                        value={newDescription}
                                                        onChange={(e) => setNewDescription(e.target.value)}
                                                        placeholder="Add a description..."
                                                        autoFocus
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                                e.preventDefault();
                                                                handleUpdateDescription(video._id);
                                                            } else if (e.key === 'Escape') {
                                                                setEditingVideo(null);
                                                                setNewDescription('');
                                                            }
                                                        }}
                                                    />
                                                    <div className="description-actions">
                                                        <button onClick={() => handleUpdateDescription(video._id)}>
                                                            Save
                                                        </button>
                                                        <button onClick={() => {
                                                            setEditingVideo(null);
                                                            setNewDescription('');
                                                        }}>
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div 
                                                    className="description-display"
                                                    onClick={() => {
                                                        setEditingVideo(video._id);
                                                        setNewDescription(video.description || '');
                                                    }}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <p>{video.description || 'Click to add a description...'}</p>
                                                    <button 
                                                        className="edit-description-btn"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setEditingVideo(video._id);
                                                            setNewDescription(video.description || '');
                                                        }}
                                                    >
                                                        <Edit2 size={14} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        {/* Cuts */}
                                        {!disableCuts && (
                                            <div className="video-cuts">
                                                <h4>Cuts</h4>
                                                <div className="cuts-list">
                                                    {(video.cuts || []).map((cut, index) => (
                                                        <div key={`${video._id}-cut-${index}`} className="cut-item">
                                                            <span>{cut.name}</span>
                                                            <video src={cut.url} controls />
                                                        </div>
                                                    ))}
                                                </div>
                                                <button 
                                                    className="add-cut-button"
                                                    onClick={() => setSelectedVideo(video)}
                                                >
                                                    <Plus size={14} />
                                                    Add Cut
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            ) : (
                <div className="videos-grid">
                    {isLoading ? (
                        <div className="shimmer-grid">
                            {[...Array(6)].map((_, index) => (
                                <ShimmerCard key={`shimmer-${index}`} />
                            ))}
                        </div>
                    ) : (
                        generatedVideos.map(video => (
                            <div key={video._id} className="video-card">
                                <div className="video-preview">
                                    <video src={video.url} controls style={{objectFit: 'contain'}}/>
                                </div>
                                <div className="video-info">
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '12px',
                                        alignContent: 'center'
                                    }}>
                                        <h3 style={{margin: '0'}}>{video.title}</h3>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button 
                                                className="download-video-btn"
                                                onClick={() => handleDownloadVideo(video)}
                                                style={{
                                                    backgroundColor: 'var(--button-bg)',
                                                    color: 'var(--button-text)',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    padding: '4px 8px',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                    opacity: '0.9',
                                                    transition: 'opacity 0.2s',
                                                    marginLeft: '10px'
                                                }}
                                                onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
                                                onMouseOut={(e) => e.currentTarget.style.opacity = '0.9'}
                                            >
                                                <Download size={14} />
                                            </button>
                                        </div>
                                    </div>
                                    <p>{video.description || 'No description'}</p>
                                    <div className="video-tags">
                                        {video.tags.map(tag => (
                                            <span key={tag} className="tag">{tag}</span>
                                        ))}
                                    </div>
                                    {!disableCuts && (
                                        <button 
                                            className="add-cut-button"
                                            onClick={() => setSelectedVideo(video)}
                                        >
                                            <Plus size={14} />
                                            Add Cut
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* Add Cut Modal */}
            {selectedVideo && (
                <div className="modal" onClick={(e) => {
                    // Only close if clicking the modal background, not its children
                    if (e.target === e.currentTarget) {
                        setSelectedVideo(null);
                    }
                }}>
                    <div className="modal-content">
                        <h3>Add Cuts for {selectedVideo.name} {selectedVideo.name}</h3>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept="video/*"
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                        />
                        <div 
                            className={`upload-area ${isDragging ? 'dragging' : ''}`}
                            onDragOver={(e) => {
                                e.preventDefault();
                                setIsDragging(true);
                            }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleCutDrop}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <div className="upload-label">
                                <Plus size={24} />
                                <span>Drag & drop video cuts here or click to select</span>
                                <span className="upload-hint">Up to 50 files at once</span>
                            </div>
                        </div>
                        <div className="modal-actions">
                            <button type="button" onClick={() => setSelectedVideo(null)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VideoManagement; 